import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  changeBudgetScenarioName,
  copyBudgetScenario,
  createBudgetScenarioId,
  generateNewCopyScenarioName,
  generateNewCustomScenarioName,
  markBudgetScenarioAsSaved,
  saveInEditModeBudgetScenario,
  updateBudgetId,
  upsertBudgetScenarioValues
} from './edit-budget-scenario.slice';
import { RootState } from 'app/store';
import {
  openNewBudgetScenarioMessage,
  overrideComparedSelectedBudgetScenario
} from './mmm-controls/mmm-control.slice';
import alert from 'common/components/alerts/alert';
import {
  closeBudgetScenarioSaveModal,
  openBudgetScenarioSaveModal
} from './save-modal/budget-scenario-save-modal.slice';
import { onSuccess } from 'features/toast/toast.slice';

const budgetScenarioListenerMiddleware = createListenerMiddleware();

budgetScenarioListenerMiddleware.startListening({
  actionCreator: saveInEditModeBudgetScenario,
  effect: async (action, listenerApi) => {
    const { budgetId, scenarioName } = action.payload;

    const state = listenerApi.getState() as RootState;

    const budgetToSave =
      state.editBudgetScenario.inProgressEditBudgetScenarios.find(
        (scenario) =>
          scenario.budgetId === budgetId &&
          scenario.scenarioName === scenarioName
      );

    if (!budgetToSave) {
      console.warn(
        `No budget scenario found with budgetId: ${budgetId} and scenarioName: ${scenarioName}`
      );
      return;
    }

    if (budgetToSave.hasNameChanged) {
      if (state.budgetScenarioSaveModal.isOpen) {
        listenerApi.dispatch(closeBudgetScenarioSaveModal());
      }

      listenerApi.dispatch(
        onSuccess({
          title: 'Budget Scenario Saved',
          description: `You have saved the budget scenario: "${budgetToSave.scenarioName}".`
        })
      );

      listenerApi.dispatch(
        markBudgetScenarioAsSaved({
          budgetId: budgetToSave.budgetId
        })
      );
    } else {
      listenerApi.dispatch(
        openBudgetScenarioSaveModal({
          budgetId: budgetToSave.budgetId,
          scenarioName: budgetToSave.scenarioName
        })
      );
    }
  }
});

budgetScenarioListenerMiddleware.startListening({
  actionCreator: changeBudgetScenarioName,
  effect: async (action, listenerApi) => {
    const { budgetId, scenarioName } = action.payload;

    const state = listenerApi.getState() as RootState;

    if (state.budgetScenarioSaveModal.isOpen) {
      const updatedBudgetId = updateBudgetId(budgetId, scenarioName);

      listenerApi.dispatch(
        saveInEditModeBudgetScenario({
          budgetId: updatedBudgetId,
          scenarioName
        })
      );
    }
  }
});

budgetScenarioListenerMiddleware.startListening({
  actionCreator: upsertBudgetScenarioValues,
  effect: (action, listenerApi) => {
    const {
      scenarioName,
      modelRunName,
      timeType,
      segmentKey,
      availableSystemModels
    } = action.payload;

    const currentBudgetId = createBudgetScenarioId(
      scenarioName,
      modelRunName,
      timeType,
      segmentKey
    );

    const state = listenerApi.getState() as RootState;

    if (IsUpsertActionCreatingANewBudgetScenario(state, currentBudgetId)) {
      listenerApi.dispatch(openNewBudgetScenarioMessage());

      if (availableSystemModels.includes(scenarioName)) {
        const newScenarioName = generateNewCustomScenarioName(
          state.editBudgetScenario.inProgressEditBudgetScenarios.some(
            (scenario) =>
              scenario.budgetId === currentBudgetId && scenario.isFirstCreation
          )
            ? state.editBudgetScenario.inProgressEditBudgetScenarios.length - 1
            : state.editBudgetScenario.inProgressEditBudgetScenarios.length,
          scenarioName
        );

        const newBudgetId = createBudgetScenarioId(
          newScenarioName,
          modelRunName,
          timeType,
          segmentKey
        );

        listenerApi.dispatch(
          overrideComparedSelectedBudgetScenario({
            budgetId: newBudgetId,
            scenarioName: newScenarioName
          })
        );
      }
    }
  }
});

budgetScenarioListenerMiddleware.startListening({
  actionCreator: copyBudgetScenario,
  effect: (action, listenerApi) => {
    const { budgetId } = action.payload;

    const state = listenerApi.getState() as RootState;
    let budgetScenarioName;

    const isSystemScenario = budgetId.startsWith('system--');

    if (isSystemScenario) {
      const budgetIdParts = budgetId.split('--');
      budgetIdParts.shift(); // Remove 'system-' prefix
      const scenarioName = budgetIdParts[0];

      const systemScenarioIndex =
        state.editBudgetScenario.availableSystemScenarios.findIndex(
          (scenario) => scenario === scenarioName
        );

      if (systemScenarioIndex === -1) {
        console.warn(`No system scenario found with name: ${scenarioName}`);
        return;
      }

      budgetScenarioName =
        state.editBudgetScenario.availableSystemScenarios[systemScenarioIndex];
    } else {
      const existingScenarioIndex =
        state.editBudgetScenario.inProgressEditBudgetScenarios.findIndex(
          (scenario) => scenario.budgetId === budgetId
        );

      if (existingScenarioIndex === -1) {
        console.warn(`No scenario found with budgetId: ${budgetId}`);
        return;
      }

      const existingScenario =
        state.editBudgetScenario.inProgressEditBudgetScenarios[
          existingScenarioIndex
        ];

      budgetScenarioName = existingScenario.scenarioName;
    }

    listenerApi.dispatch(openNewBudgetScenarioMessage());

    const newBudgetScenarioName = generateNewCopyScenarioName(
      state.editBudgetScenario.inProgressEditBudgetScenarios.length,
      budgetScenarioName
    );

    const newBudgetId = updateBudgetId(budgetId, newBudgetScenarioName);

    listenerApi.dispatch(
      overrideComparedSelectedBudgetScenario({
        budgetId: newBudgetId,
        scenarioName: newBudgetScenarioName
      })
    );
  }
});

export const budgetScenarioListener =
  budgetScenarioListenerMiddleware.middleware;

function IsUpsertActionCreatingANewBudgetScenario(
  state: RootState,
  currentBudgetId: string
): boolean {
  return (
    !state.editBudgetScenario.inProgressEditBudgetScenarios.some(
      (scenario) => scenario.budgetId === currentBudgetId
    ) ||
    state.editBudgetScenario.inProgressEditBudgetScenarios.some(
      (scenario) =>
        scenario.budgetId === currentBudgetId && scenario.isFirstCreation
    )
  );
}
